import { useEffect, useRef } from "react"

const DENSITY = 50
const MAX_PEOPLE = 40
const TOO_CLOSE = 60
const MOVE_STEPS = 60
const STEP = TOO_CLOSE / MOVE_STEPS
const FRAME_DURATION = 1000 / 60
const PERSON_WIDTH = 10
const PERSON_HEIGHT = 50
const DIRECTIONS = ["tl", "tr", "br", "bl"] as const
const MOVEMENTS = ["standing", "walking", "falling", "leveling"] as const

function randomDirection() {
  return DIRECTIONS[Math.floor(Math.random() * DIRECTIONS.length)]
}

function initialLevel() {
  const exponentialRandom = -Math.log(1 - Math.random())
  return Math.max(1, Math.min(Math.ceil(exponentialRandom * 0.8), 5))
}

function reducedMotionEnabled() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

function removeMovementClasses(person: HTMLElement) {
  MOVEMENTS.forEach((className) => {
    person.classList.remove(className)
  })
}

function removeDirectionClasses(person: HTMLElement) {
  DIRECTIONS.forEach((className) => {
    person.classList.remove(className)
  })
}

function updateDirection(person: HTMLElement) {
  removeDirectionClasses(person)
  const targetTB = Number(person.offsetTop) > Number(person.dataset.top) ? "t" : "b"
  const targetLR = Number(person.offsetLeft) > Number(person.dataset.left) ? "l" : "r"
  person.classList.add(`${targetTB}${targetLR}`)
}

function setLevel(person: HTMLElement, newLevel: number) {
  person.dataset.level = newLevel.toString()
  ;[1, 2, 3, 4, 5].forEach((level) => {
    person.classList.remove(`level-${level}`)
  })
  person.classList.add(`level-${newLevel}`)
}

function walkTo(person: HTMLElement, xAdd = 0, yAdd = 0) {
  const targetX = person.offsetLeft + xAdd
  const targetY = person.offsetTop + yAdd
  person.dataset.left = targetX.toString()
  person.dataset.top = targetY.toString()
  updateDirection(person)
}

export default function PeopleScene() {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container || reducedMotionEnabled()) return

    let peopleCount = 0
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    let animationFrameId: number | undefined
    let cancelled = false
    const timeouts: ReturnType<typeof setTimeout>[] = []

    const scheduleTimeout = (callback: () => void, delay: number) => {
      const id = setTimeout(callback, delay)
      timeouts.push(id)
      return id
    }

    const completeFall = (id: number) => {
      const person = document.getElementById(`person-${id}`) as HTMLElement | null

      if (!person) return

      person.classList.remove("falling")
      person.style.animationDelay = "0ms"
      person.classList.add("standing")
    }

    const createPerson = (targetContainer: HTMLElement, index = 0) => {
      peopleCount += 1
      const id = peopleCount
      const width = targetContainer.clientWidth || 0
      const height = targetContainer.clientHeight || 0
      const top = Math.round(Math.random() * (height * 1.1) - PERSON_HEIGHT)
      const left = Math.round(Math.random() * (width * 1.1) - PERSON_WIDTH)
      const person = document.createElement("div")

      person.id = `person-${id}`
      person.classList.add("person", randomDirection(), "falling", Math.random() > 0.5 ? "male" : "female")
      setLevel(person, initialLevel())
      person.dataset.top = top.toString()
      person.dataset.left = left.toString()
      person.style.top = `${top}px`
      person.style.left = `${left}px`
      person.style.zIndex = ((top + 100) * 100 + left).toString()
      person.style.animationDelay = `${id * 100}ms`

      targetContainer.appendChild(person)
      scheduleTimeout(() => completeFall(id), 2000 + index * 100)
    }

    const levelUp = (person: HTMLElement) => {
      const currentLevel = person.dataset.level ? Number(person.dataset.level) : 1

      if (currentLevel < 5) {
        const newLevel = currentLevel + 1
        removeMovementClasses(person)
        person.classList.add("leveling")

        scheduleTimeout(() => {
          removeMovementClasses(person)
          person.classList.add("standing")
          setLevel(person, newLevel)
        }, 1000)
        return
      }

      if (Math.random() < 0.2) {
        const xMultiplier = Math.random() > 0.5 ? 1 : -1
        walkTo(person, xMultiplier * 1000, -1000)

        scheduleTimeout(() => {
          person.remove()
          if (!cancelled) createPerson(container)
        }, 10000)
      }
    }

    const moveAwayFromMouse = (event: MouseEvent) => {
      const rect = container.getBoundingClientRect()
      const { top, bottom, left, right } = rect
      const { clientX, clientY } = event
      const mouseX = clientX - left
      const mouseY = clientY - top

      if (clientX <= left || clientX >= right || clientY <= top || clientY >= bottom) return

      const people = container.querySelectorAll<HTMLElement>(".person")

      people.forEach((person) => {
        const centerX = person.offsetLeft + person.clientWidth / 2
        const centerY = person.offsetTop + person.clientHeight / 2

        if (Math.abs(mouseX - centerX) >= TOO_CLOSE || Math.abs(mouseY - centerY) >= TOO_CLOSE) return

        const rightLeft = mouseX > centerX ? -1 : 1
        const topBottom = mouseY > centerY ? -1 : 1
        person.dataset.left = (person.offsetLeft + rightLeft * TOO_CLOSE).toString()
        person.dataset.top = (person.offsetTop + topBottom * TOO_CLOSE).toString()
        updateDirection(person)
      })
    }

    const updateAnimation = () => {
      const people = container.querySelectorAll<HTMLElement>(".person")
      const containerWidth = container.clientWidth || 0
      const containerHeight = container.clientHeight || 0

      people.forEach((person) => {
        let directionLR = Number(person.dataset.left) - person.offsetLeft
        let directionTB = Number(person.dataset.top) - person.offsetTop
        const falling = person.classList.contains("falling")
        let leveling = person.classList.contains("leveling")

        if (!falling && !leveling && directionLR === 0 && directionTB === 0 && Math.random() < 0.01) {
          if (Math.random() < 0.1) {
            levelUp(person)
            leveling = true
          } else {
            const targetX =
              person.offsetLeft < -TOO_CLOSE
                ? person.offsetLeft + TOO_CLOSE
                : person.offsetLeft > containerWidth + TOO_CLOSE
                  ? person.offsetLeft - TOO_CLOSE
                  : Math.random() > 0.5
                    ? person.offsetLeft + TOO_CLOSE
                    : person.offsetLeft - TOO_CLOSE
            const targetY =
              person.offsetTop < -TOO_CLOSE
                ? person.offsetTop + TOO_CLOSE
                : person.offsetTop > containerHeight + TOO_CLOSE
                  ? person.offsetTop - TOO_CLOSE
                  : Math.random() > 0.5
                    ? person.offsetTop + TOO_CLOSE
                    : person.offsetTop - TOO_CLOSE

            walkTo(person, targetX, targetY)
            person.dataset.left = targetX.toString()
            person.dataset.top = targetY.toString()
            directionLR = Number(person.dataset.left) - person.offsetLeft
            directionTB = Number(person.dataset.top) - person.offsetTop
            updateDirection(person)
          }
        }

        let nextLeft = person.offsetLeft
        let nextTop = person.offsetTop

        if (directionLR > 0) nextLeft = person.offsetLeft + Math.min(directionLR, STEP)
        if (directionLR < 0) nextLeft = person.offsetLeft + Math.max(directionLR, -STEP)
        if (directionTB > 0) nextTop = person.offsetTop + Math.min(directionTB, STEP)
        if (directionTB < 0) nextTop = person.offsetTop + Math.max(directionTB, -STEP)

        if (directionLR !== 0) person.style.left = `${nextLeft}px`
        if (directionTB !== 0) person.style.top = `${nextTop}px`
        person.style.zIndex = ((nextTop + 100) * 100 + nextLeft).toString()

        if (!falling && !leveling) {
          removeMovementClasses(person)
          person.classList.add(directionLR === 0 && directionTB === 0 ? "standing" : "walking")
        }
      })

      timeoutId = setTimeout(() => {
        if (!cancelled) {
          animationFrameId = requestAnimationFrame(updateAnimation)
        }
      }, FRAME_DURATION)
    }

    const createPeople = () => {
      const width = container.clientWidth || 0
      const height = container.clientHeight || 0
      const count = Math.min(Math.round(DENSITY * ((width * height) / 1_000_000)), MAX_PEOPLE)

      for (let index = 0; index < count; index += 1) {
        createPerson(container, index)
      }

      animationFrameId = requestAnimationFrame(updateAnimation)
    }

    document.addEventListener("mousemove", moveAwayFromMouse)
    createPeople()

    return () => {
      cancelled = true
      timeouts.forEach(clearTimeout)
      if (timeoutId) clearTimeout(timeoutId)
      if (animationFrameId) cancelAnimationFrame(animationFrameId)
      document.removeEventListener("mousemove", moveAwayFromMouse)
      container.replaceChildren()
    }
  }, [])

  return (
    <div
      aria-hidden="true"
      className="people-container"
      id="people-container"
      ref={containerRef}
    />
  )
}
